import { logger } from "@/core/logging";

import { IStorageAdapter } from "../IStorageAdapter";

const LEGACY_JWT_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

export default class SessionStorageAdapter implements IStorageAdapter {
  getItem<T>(key: string): Promise<T | null> {
    const storage = this.getStorage();
    if (!storage) {
      return Promise.resolve(null);
    }

    const stored = storage.getItem(key);
    if (stored === null) {
      return Promise.resolve(null);
    }

    try {
      return Promise.resolve(JSON.parse(stored) as T);
    } catch (error) {
      const trimmed = stored.trim();
      if (LEGACY_JWT_PATTERN.test(trimmed)) {
        return Promise.resolve(trimmed as unknown as T);
      }
      logger.error("session_storage_get_item_failed", { key }, error as Error);
      return Promise.resolve(null);
    }
  }

  getItemSync<T>(key: string): T | null {
    const storage = this.getStorage();
    if (!storage) {
      return null;
    }

    const stored = storage.getItem(key);
    if (stored === null) {
      return null;
    }

    try {
      return JSON.parse(stored) as T;
    } catch (error) {
      const trimmed = stored.trim();
      if (LEGACY_JWT_PATTERN.test(trimmed)) {
        return trimmed as unknown as T;
      }
      logger.error("session_storage_get_item_sync_failed", { key }, error as Error);
      return null;
    }
  }

  setItem<T>(key: string, value: T): Promise<void> {
    const storage = this.getStorage();
    if (!storage) {
      return Promise.resolve();
    }

    const serialized = this.stringifySafely(key, value);
    if (serialized === null) {
      return Promise.resolve();
    }

    try {
      storage.setItem(key, serialized);
      return Promise.resolve();
    } catch (error) {
      logger.error("session_storage_set_item_failed", { key }, error as Error);
      return Promise.resolve();
    }
  }

  setItemSync<T>(key: string, value: T): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    const serialized = this.stringifySafely(key, value);
    if (serialized === null) {
      return;
    }

    try {
      storage.setItem(key, serialized);
    } catch (error) {
      logger.error("session_storage_set_item_sync_failed", { key }, error as Error);
    }
  }

  removeItem(key: string): Promise<void> {
    const storage = this.getStorage();
    if (!storage) {
      return Promise.resolve();
    }

    return Promise.resolve(storage.removeItem(key));
  }

  removeItemSync(key: string): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    storage.removeItem(key);
  }

  removeItemsByPrefixSync(prefixes: readonly string[]): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    const keysToRemove = this.getStorageKeys(storage).filter((key) =>
      prefixes.some((prefix) => key.startsWith(prefix)),
    );

    for (const key of keysToRemove) {
      storage.removeItem(key);
    }
  }

  clear(): Promise<void> {
    const storage = this.getStorage();
    if (!storage) {
      return Promise.resolve();
    }

    return Promise.resolve(storage.clear());
  }

  private stringifySafely(key: string, value: unknown): string | null {
    try {
      return JSON.stringify(value);
    } catch (error) {
      logger.warn("session_storage_serialize_failed", { key });
      return null;
    }
  }

  private getStorage(): Storage | null {
    return globalThis.sessionStorage;
  }

  private getStorageKeys(storage: Storage): string[] {
    const keys: string[] = [];

    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key) {
        keys.push(key);
      }
    }

    return keys;
  }
}
